import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Select,
  InputNumber,
  Upload,
  Button,
  notification,
  Row,
  Col,
  Typography,
  Space,
  message,
  Modal,
  Spin,
  Radio
} from "antd";
import { ExclamationCircleFilled, UploadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { prizesAPI, currenciesAPI } from "@api";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { StockTypes } from "components/PostCard/PostCard";

const { TextArea } = Input;
const { Title } = Typography;
const { confirm } = Modal;
const { Item } = Form;

const CreateEditPrizeMobile = () => {

  const [fileList, setFileList] = useState([]);
  const [form] = Form.useForm();
  const selectedCurrency = Form.useWatch("currencyId", form);
  const { t } = useTranslation("common");
  const { t: tError } = useTranslation("error");
  const navigate = useNavigate();
  const { id } = useParams()

  const { data: prize, isLoading: loadingPrize } = useQuery({
    queryKey: ['prize', id],
    queryFn: () => prizesAPI.getOne(id).then(({ data }) => data),
    onError: ({ response }) =>
      notification.error({
        message: tError(response.data.message),
      }),
    enabled: !!id,
  })

  const { data: currencies } = useQuery({
    queryKey: ["currencyOptions"],
    queryFn: () =>
      currenciesAPI.getCurrencies().then(({ data }) =>
        data.map((currency) => ({
          label: currency.symbol,
          value: currency.id,
        }))
      ),
    onError: ({ response }) =>
      notification.error({
        message: tError(response.data.message),
      }),
  });

  const { mutate, isLoading: confirmLoading } = useMutation({
    mutationFn: ({ id, values }) => {
      if (prize) {
        return prizesAPI.updatePrize(id, values);
      } else {
        return prizesAPI.createPrize(values);
      }
    },
    onSuccess: () => {
      form.resetFields();
      setFileList([]);
      navigate('/prizes')
      message.success(t(`prizeTable.notification.${prize ? "edit" : "create"}`));
    },
    onError: ({ response }) =>
      notification.error({
        message: tError(response.data.message),
      }),
  });

  const { mutate: deletePrizeMutation, isLoading: loadingDelete } = useMutation({
    mutationFn: prizesAPI.deletePrize,
    onSuccess: () => {
      navigate('/prizes')
      message.success(t("prizeTable.delete.onSucces"))
    },
    onError: ({ response }) =>
      notification.error({
        message: tError(response.data.message),
      }),
  });

  const selectedStockType = Form.useWatch("stockType", form);

  const showDeletePrizeConfirm = (prize) => {
    confirm({
      title: t("prizeTable.delete.confirm"),
      icon: <ExclamationCircleFilled />,
      content: t("prizeTable.delete.description"),
      okType: "danger",
      okText: t("prizeTable.delete.ok"),
      cancelText: t("prizeTable.delete.cancel"),
      maskClosable: true,
      centered: true,
      onOk: () => deletePrizeMutation(prize.id),
    });
  };

  useEffect(() => {
    if (prize) {
      form.setFieldsValue({
        ...prize,
        currencyId: prize.currency?.id,
      });
      setFileList([
        {
          uid: prize?.imageUrl,
          thumbUrl: prize?.imageUrl,
          url: prize?.imageUrl,
          name: prize?.name
        },
      ])
    } else {
      setFileList([]);
    }
  }, [prize]);


  return (
    <Row style={{ background: '#fff' }}>
      <Row style={{ margin: '16px' }}>
        <Col span={24}>
          <Title level={4} style={{ margin: 0 }} > {prize ? t("prizeTable.edit") : t("prizeTable.create")} </Title>
        </Col>
        <Spin spinning={loadingPrize && !!id} size="small">
          <Form
            form={form}
            name="prizeForm"
            onFinish={(values) => mutate({ id: prize?.id, values })}
            autoComplete="off"
            scrollToFirstError
            style={{ width: '100%' }}
            requiredMark={false}
          >
            <Row justify={'start'} gutter={[16, 16]}>
              <Col span={24}>
                <Item
                  style={{ margin: 0 }}
                  label={t("prizeTable.name")}
                  name="name"
                  rules={[{ required: true, message: t("prizeTable.form.name") }]}
                >
                  <Input placeholder={t("prizeTable.form.namePlaceholder")} />
                </Item>
              </Col>

              <Col span={24}>
                <Item
                  style={{ margin: 0 }}
                  label={t("prizeTable.description")}
                  name="description"
                  rules={[
                    { required: true, message: t("prizeTable.form.description") },
                  ]}
                >
                  <TextArea placeholder={t("prizeTable.form.descriptionPlaceholder")} rows={4} style={{ resize: "none" }} />
                </Item>
              </Col>

              <Col span={24}>
                <Item
                  style={{ margin: 0 }}
                  label={t("prizeTable.price")}
                  name="price"
                  rules={[{ required: true, message: t("prizeTable.form.price") }]}
                >
                  <InputNumber placeholder={t("prizeTable.form.pricePlaceholder")} step={5} min={0} style={{ width: "100%" }} />
                </Item>
              </Col>

              <Col span={24}>
                <Item
                  style={{ margin: 0 }}
                  label={t("prizeTable.currency")}
                  name="currencyId"
                  rules={[
                    {
                      required: true,
                      message: t("prizeTable.form.currency"),
                    },
                  ]}
                >
                  <Select
                    placeholder={t("prizeTable.form.currencyPlaceholder")}
                    showSearch
                    optionFilterProp="children"
                    allowClear
                    filterOption={(input, option) =>
                      option?.label.toLowerCase().includes(input.toLowerCase())
                    }
                    options={currencies}
                  />
                </Item>
              </Col>

              <Col span={24}>
                <Item
                  style={{ margin: 0 }}
                  label={t("prizeTable.cost")}
                  name="cost"
                  rules={[{ required: true, message: t("prizeTable.form.cost") }]}
                >
                  <InputNumber
                    min={0}
                    placeholder={t("prizeTable.form.costPlaceholder")}
                    style={{ width: "100%" }}
                    addonBefore={
                      currencies?.find(
                        (currency) => currency.value === selectedCurrency
                      )?.label
                    }
                  />
                </Item>
              </Col>
              <Col span={24}>
                <Item
                  name="available"
                  label={t("prizeTable.available")}
                  style={{ margin: 0 }}
                  rules={[{ required: true, message: t("prizeTable.form.available") }]}
                >
                  <Radio.Group>

                    <Radio value={true}> {t("prizeTable.form.ratioAvailable")} </Radio>
                    <Radio value={false}> {t("prizeTable.form.ratioUnavailable")} </Radio>
                  </Radio.Group>
                </Item>
              </Col>
              <Col span={24}>
                <Item
                  style={{ margin: 0 }}
                  label={t("prizeTable.stockType")}
                  name="stockType"
                  rules={[{ required: true, message: t("prizeTable.form.stockType") }]}
                >
                  <Select placeholder={t("prizeTable.form.stockTypePlaceholder")}>
                    <Select.Option value="STOCK">{StockTypes.STOCK}</Select.Option>
                    <Select.Option value="DEMAND">{StockTypes.DEMAND}</Select.Option>
                  </Select>
                </Item>
              </Col>

              {selectedStockType === StockTypes.STOCK && (
                <Col span={24}>
                  <Item
                    style={{ margin: 0 }}
                    label={t("prizeTable.stockAmount")}
                    name="stockAmount"
                    rules={[
                      { required: true, message: t("prizeTable.form.stockAmount") },
                    ]}
                  >
                    <InputNumber placeholder={t("prizeTable.form.stockAmountPlaceholder")} min={0} style={{ width: "100%" }} />
                  </Item>
                </Col>
              )}

              <Col span={24}>
                <Item
                  style={{ margin: 0 }}
                  label={t("prizeTable.imageUrl")}
                  name="attachment"
                  rules={[
                    {
                      required: !prize,
                      message: t("prizeTable.form.imageUrl"),
                    },
                  ]}
                  getValueFromEvent={({ file }) => file}
                >
                  <Upload
                    listType="picture"
                    maxCount={1}
                    accept="image/png, image/jpeg, image/jpg"
                    beforeUpload={() => false}
                    multiple={false}
                    fileList={fileList}
                    onChange={({ fileList }) => setFileList(fileList)}
                  >
                    <Button icon={<UploadOutlined />}>
                      {t("userTable.form.btnUpload")}
                    </Button>
                  </Upload>
                </Item>
              </Col>

              <Col span={24}>
                <Item
                  label={t("prizeTable.observation")}
                  name="observation"
                >
                  <TextArea placeholder={t("prizeTable.form.observationPlaceholder")} rows={4} style={{ resize: "none" }} />
                </Item>
              </Col>
            </Row>
            <Col span={24}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button style={{ width: '100%' }} type="primary" htmlType="submit" loading={confirmLoading | loadingDelete} > {prize ? t("prizeTable.form.btnSaveChanges") : t("prizeTable.form.btnCreate")} </Button>
                {prize ?
                  <>
                    <Button style={{ width: '100%' }} onClick={() => navigate('/prizes')} loading={confirmLoading | loadingDelete}> {t("prizeTable.form.btnDiscardChanges")} </Button>
                    <Button style={{ width: '100%' }} danger onClick={() => showDeletePrizeConfirm(prize)} loading={confirmLoading | loadingDelete} > {t("prizeTable.delete.default")} </Button>
                  </>
                  :
                  <Button style={{ width: '100%' }} onClick={() => navigate('/prizes')} loading={confirmLoading | loadingDelete} > {t("prizeTable.form.btnCancel")} </Button>
                }
              </Space>
            </Col>
          </Form>
        </Spin>
      </Row>
    </Row>
  )
}

export default CreateEditPrizeMobile;